// ─── Agency Reporting export columns ─────────────────────
//
// One catalogue, three consumers: the server-side CSV (`exportReportCsv`), the
// client-side Excel sheet, and the print/PDF view. They used to each build their
// own header + row arrays, which is how the CSV drifted from the screen (date
// only vs date+time, raw type name vs display label). Everything now renders
// through `reportExportCell`, so whatever the agency ticks in the export dialog
// comes out identical in all three formats.
//
// Order here IS the column order in the file. The patient-identity extras (DOB /
// phone / admission id) sit next to Patient so a selection reads as one block;
// leaving them unticked reproduces the original seven-column export byte for
// byte.
//
// Phone is two columns, not one. 44% of patients with a recent visit have more
// than one number on file, so a single column silently drops real data — but
// concatenating them all would be worse: most extras are just another mobile for
// the same person, while a minority belong to a caregiver, an aide or a relative,
// and merging those into a column headed "Phone Number" misrepresents whose
// number it is. So "Phone Number" stays the one number to call (the primary), and
// "Additional Phones" carries the rest with their labels.
//
// Aide Present is also two columns (the Yes/No, then the reason and note) but
// ONE checkbox: the pair only means something together, so the dialog offers it
// as a single option and normalizeReportExportColumns never lets half of it
// through. Both stay blank on visits without a Supervisory Evaluation form.
import { aidePresentNotes, aidePresentYesNo } from "./aide-present.js";
export const REPORT_EXPORT_COLUMNS = [
    { key: "id", label: "ID#", isDefault: true },
    { key: "created", label: "Created", isDefault: true },
    { key: "patient", label: "Patient", isDefault: true },
    { key: "dob", label: "Date of Birth", isDefault: false },
    { key: "phone", label: "Phone Number", isDefault: false },
    { key: "additionalPhones", label: "Additional Phones", isDefault: false },
    { key: "admissionId", label: "Admission ID", isDefault: false },
    { key: "type", label: "Type", isDefault: true },
    { key: "status", label: "Status", isDefault: true },
    { key: "reviewStatus", label: "Review Status", isDefault: true },
    { key: "visitDate", label: "Visit Date", isDefault: true },
    { key: "aidePresent", label: "Aide Present", isDefault: false },
    { key: "aidePresentNotes", label: "Aide Present Notes", isDefault: false }
];
export const REPORT_EXPORT_COLUMN_KEYS = REPORT_EXPORT_COLUMNS.map((c) => c.key);
export const DEFAULT_REPORT_EXPORT_COLUMNS = REPORT_EXPORT_COLUMNS.filter((c) => c.isDefault).map((c) => c.key);
// Columns that share one checkbox. Every key here is selected together or not
// at all.
const REPORT_EXPORT_COLUMN_GROUPS = [{ label: "Aide Present + Notes", keys: ["aidePresent", "aidePresentNotes"] }];
/** The dialog's checkboxes, in column order; a group sits where its first column does. */
export const REPORT_EXPORT_COLUMN_OPTIONS = REPORT_EXPORT_COLUMNS.flatMap((column) => {
    const group = REPORT_EXPORT_COLUMN_GROUPS.find((g) => g.keys.includes(column.key));
    if (!group) {
        return [{ label: column.label, keys: [column.key], isDefault: column.isDefault }];
    }
    return group.keys[0] === column.key
        ? [{ label: group.label, keys: group.keys, isDefault: column.isDefault }]
        : [];
});
export function isReportExportColumnKey(value) {
    return REPORT_EXPORT_COLUMN_KEYS.includes(value);
}
/**
 * Drop unknown keys, de-duplicate, complete any half-selected group, and re-sort
 * into the canonical order above. An empty or entirely-unrecognised selection
 * falls back to the defaults rather than producing a headerless file.
 */
export function normalizeReportExportColumns(keys) {
    if (!keys || keys.length === 0) {
        return [...DEFAULT_REPORT_EXPORT_COLUMNS];
    }
    const wanted = new Set(keys.filter(isReportExportColumnKey));
    for (const group of REPORT_EXPORT_COLUMN_GROUPS) {
        if (group.keys.some((k) => wanted.has(k))) {
            group.keys.forEach((k) => wanted.add(k));
        }
    }
    const ordered = REPORT_EXPORT_COLUMN_KEYS.filter((k) => wanted.has(k));
    return ordered.length > 0 ? ordered : [...DEFAULT_REPORT_EXPORT_COLUMNS];
}
export function reportExportColumnLabel(key) {
    return REPORT_EXPORT_COLUMNS.find((c) => c.key === key)?.label ?? key;
}
/** Zero-padded m/d/y, so a spreadsheet sorts the column as written. */
function formatDate(value, timeZone) {
    if (!value) {
        return "";
    }
    return new Date(value).toLocaleDateString("en-US", {
        timeZone,
        month: "2-digit",
        day: "2-digit",
        year: "numeric"
    });
}
function formatDateTime(value, timeZone) {
    if (!value) {
        return "";
    }
    return new Date(value).toLocaleString("en-US", {
        timeZone,
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true
    });
}
/**
 * A `date` column arrives as "YYYY-MM-DD" with no zone. Passing that through
 * `new Date()` parses it as midnight UTC, which prints as the previous day west
 * of Greenwich — the same bug that mis-dated visit PDFs. Split it by hand.
 */
function formatDateOnlyString(value) {
    if (!value) {
        return "";
    }
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (!match) {
        return value;
    }
    const [, year, month, day] = match;
    return `${month}/${day}/${year}`;
}
/**
 * Effective visit date/time: prefer the real `startTime` rendered in the
 * API-runtime zone (America/New_York), else the tz-aware staffing window in the
 * visit's own zone. Mirrors `formatVisitDateTime` on the reporting screen.
 */
function formatVisitDateTime(row) {
    if (row.startTime) {
        return formatDateTime(row.startTime, "America/New_York");
    }
    return formatDateTime(row.scheduledStart, row.timezone);
}
function titleCase(value) {
    return value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
/** (212) 555-0134 for a stored E.164 number; anything else passes through. */
function formatPhone(value) {
    if (!value) {
        return "";
    }
    const match = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(value);
    return match ? `(${match[1]}) ${match[2]}-${match[3]}` : value;
}
// Labels that describe the KIND of line rather than whose it is. Printing
// "(347) 555-1234 (Mobile)" tells an agency nothing; "(347) 555-1234 (Caregiver)"
// tells them who picks up. Prod labels are overwhelmingly MOBILE/Mobile, with a
// long tail of "caregiver", "Aide", "brother", "Husband" and personal names —
// those are the ones worth carrying through.
const GENERIC_PHONE_LABELS = new Set([
    "mobile",
    "cell",
    "tel",
    "phone",
    "home",
    "landline",
    "patient",
    "main contact"
]);
function isInformativeLabel(label) {
    if (!label) {
        return false;
    }
    const normalized = label.trim().toLowerCase();
    if (normalized === "" || GENERIC_PHONE_LABELS.has(normalized)) {
        return false;
    }
    // "#3", "Mobile 2" — positional labels from the legacy import.
    if (/^#\d+$/.test(normalized) || /^(mobile|phone|tel)\s*\d+$/.test(normalized)) {
        return false;
    }
    // "MARIA COLLAZO (The Patient)" — a name, but it IS the patient.
    if (normalized.endsWith("(the patient)")) {
        return false;
    }
    return true;
}
function formatPhoneWithLabel(phone) {
    const number = formatPhone(phone.number);
    return isInformativeLabel(phone.label) ? `${number} (${phone.label?.trim()})` : number;
}
/**
 * Primary first, then the order the API sent (oldest first). The API already
 * sorts this way; re-sorting here keeps the renderer correct on its own and
 * covers the handful of patients with numbers but no primary flag — for those,
 * the first on file becomes the "Phone Number".
 */
function orderedPhones(row) {
    const phones = row.patient.phones ?? [];
    return phones
        .map((phone, index) => ({ phone, index }))
        .sort((a, b) => {
        const primary = Number(b.phone.isPrimary ?? false) - Number(a.phone.isPrimary ?? false);
        return primary !== 0 ? primary : a.index - b.index;
    })
        .map((entry) => entry.phone);
}
export function reportExportCell(key, row) {
    switch (key) {
        case "id":
            return String(row.id);
        case "created":
            return formatDate(row.createdAt);
        case "patient":
            return `${row.patient.firstName} ${row.patient.lastName}`;
        case "dob":
            return formatDateOnlyString(row.patient.dob);
        case "phone":
            return formatPhone(orderedPhones(row)[0]?.number);
        case "additionalPhones":
            return orderedPhones(row).slice(1).map(formatPhoneWithLabel).join(", ");
        case "admissionId":
            return row.patient.externalId ?? "";
        case "type":
            return row.visitTypeDisplayName ?? row.visitTypeName;
        case "status":
            return titleCase(row.status);
        case "reviewStatus":
            return row.reviewStatus ? titleCase(row.reviewStatus) : "N/A";
        case "visitDate":
            return formatVisitDateTime(row);
        case "aidePresent":
            return aidePresentYesNo(row.aidePresent);
        case "aidePresentNotes":
            return aidePresentNotes(row.aidePresent);
    }
}
export function reportExportHeader(columns) {
    return columns.map(reportExportColumnLabel);
}
export function reportExportRow(columns, row) {
    return columns.map((key) => reportExportCell(key, row));
}
//# sourceMappingURL=report-export-columns.js.map